// Editorial chart tokens, as CSS variables generated from panda.config.ts
// (colors.ed.*, fonts.*). SVG attributes can't take Panda style objects, so
// the figures read these instead of hard-coding hex values.
//
// Color rule: marks default to `mark` (gray) or `ink`. Spend `emph` (azure)
// or `caution` (red) on the one series, person or point the figure's title
// or annotation calls out, and nothing else.
export const ed = {
  paper: "var(--colors-ed-paper)",
  panel: "var(--colors-ed-panel)",
  ink: "var(--colors-ed-ink)",
  ink2: "var(--colors-ed-ink2)",
  ink3: "var(--colors-ed-ink3)",
  rule: "var(--colors-ed-rule)",
  grid: "var(--colors-ed-grid)",
  mark: "var(--colors-ed-mark)",
  markStrong: "var(--colors-ed-mark-strong)",
  emph: "var(--colors-ed-emph)",
  caution: "var(--colors-ed-caution)",
  serif: "var(--fonts-serif)",
  sans: "var(--fonts-sans)",
  mono: "var(--fonts-mono)",
} as const;

/** SVG text styles. Numbers are always mono with tabular figures. */
export const svgText = {
  label: {
    fontFamily: ed.sans,
    fontSize: 12,
    fill: ed.ink2,
  },
  strong: {
    fontFamily: ed.sans,
    fontSize: 12,
    fontWeight: 600,
    fill: ed.ink,
  },
  num: {
    fontFamily: ed.mono,
    fontSize: 11.5,
    fill: ed.ink2,
    fontVariantNumeric: "tabular-nums",
  },
  numStrong: {
    fontFamily: ed.mono,
    fontSize: 11.5,
    fontWeight: 600,
    fill: ed.ink,
    fontVariantNumeric: "tabular-nums",
  },
  axis: {
    fontFamily: ed.mono,
    fontSize: 10.5,
    fill: ed.ink3,
    fontVariantNumeric: "tabular-nums",
  },
  note: {
    fontFamily: ed.sans,
    fontSize: 11.5,
    fill: ed.ink,
  },
} as const;

/** A tick step that lands on 1, 2 or 5 times a power of ten. */
export function niceStep(max: number, targetTicks = 4): number {
  if (max <= 0) return 1;
  const raw = max / targetTicks;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

export function niceMax(max: number, targetTicks = 4): number {
  const step = niceStep(max, targetTicks);
  return Math.max(step, Math.ceil(max / step) * step);
}

export function ticks(max: number, targetTicks = 4): number[] {
  const step = niceStep(max, targetTicks);
  const top = niceMax(max, targetTicks);
  const out: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) out.push(Math.round(v * 100) / 100);
  return out;
}

/** Truncate a label to fit a column, by characters. */
export function clip(label: string, max: number): string {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

/** Grays for stacked segments that aren't the point of the figure. */
export const STACK_GRAYS = [ed.markStrong, ed.mark, ed.rule, ed.grid] as const;
